import { useEffect, useRef } from 'react';
import { getCurrentOnboardingState } from '../services/chat-api.service';
import {
  createOnboardingDisplayMessage,
  createFallbackOnboardingMessage,
} from '../utils/chat-helpers';
import { useIsOnboarding } from './useIsOnboarding';
import { DisplayMode, SuperBarViewState } from '../context/use-chat-context';
import {
  type Session,
  type Store,
  OnboardingInitializationState,
  OnboardingStep,
} from '../services/types';
import type { SuperbarEventDispatcher } from './useSuperbarEventDispatcher';

export const SESSION_TOKEN_NAME = 'caddie-session-token';

interface UseOnboardingInitializationProps {
  currentSession?: Session;
  store?: Store;
  onboardingStep?: OnboardingStep;
  searchId: string | null;
  urlSearchQuery?: string;
  setDisplayMessage: (message: any) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  setSuperBarViewState: (state: SuperBarViewState) => void;
  setOnboardingStep: (state: OnboardingStep) => void;
  eventDispatcher: SuperbarEventDispatcher;
}

/**
 * Hook for handling onboarding initialization logic in ChatView
 * Manages the initial onboarding message display and state setup
 *
 * Uses a state machine to ensure initialization runs exactly once:
 * - IDLE: Not started yet, waiting for session and store
 * - PENDING: Currently fetching onboarding state from API
 * - DONE: Initialization completed, won't run again
 *
 * This hook only handles the initial welcome message setup. The ongoing
 * onboarding conversation flow is managed separately by onboardingStep.
 */
export function useOnboardingInitialization({
  currentSession,
  store,
  onboardingStep,
  searchId,
  urlSearchQuery,
  setDisplayMessage,
  setDisplayMode,
  setSuperBarViewState,
  setOnboardingStep,
  eventDispatcher,
}: UseOnboardingInitializationProps) {
  const stateRef = useRef<OnboardingInitializationState>(
    OnboardingInitializationState.IDLE
  );

  const isOnboarding = useIsOnboarding({
    store,
    currentSession,
    onboardingStep,
    searchId,
    urlSearchQuery,
  });

  useEffect(() => {
    if (!isOnboarding) return;

    // Must have session to make API call to getCurrentOnboardingState
    if (!currentSession?.id) {
      return;
    }

    // Prevent running if already initialized (permanent state)
    if (stateRef.current === OnboardingInitializationState.DONE) {
      return;
    }

    // Prevent concurrent fetches (temporary state)
    if (stateRef.current === OnboardingInitializationState.PENDING) {
      return;
    }

    const checkOnboardingState = async () => {
      // Mark as fetching to prevent concurrent fetches
      stateRef.current = OnboardingInitializationState.PENDING;

      try {
        // Get AI-generated onboarding message based on current state and history
        const onboardingState = await getCurrentOnboardingState({
          session: currentSession,
          store: store as Store,
        });

        // Always dispatch the onboarding step event, even if onboarding is not active
        // This ensures progress bars and UI elements in superserve-ui stay in sync
        if (onboardingState.onboardingStep) {
          setOnboardingStep(onboardingState.onboardingStep);

          // Dispatch event to notify superserve-ui using the event dispatcher
          // This ensures the event is queued until the listener is ready
          eventDispatcher.dispatchMessageReceived({
            onboardingStep: onboardingState.onboardingStep,
          });
        }

        // Only show the onboarding UI if onboarding is active
        if (isOnboarding) {
          // Show welcome message UI
          const message = createOnboardingDisplayMessage(onboardingState);
          setDisplayMessage(message);
          setDisplayMode(DisplayMode.ASSISTANT);
          setSuperBarViewState(SuperBarViewState.DEFAULT);
        }

        // Mark as completed successfully
        stateRef.current = OnboardingInitializationState.DONE;
      } catch (error) {
        console.error(
          'useOnboardingInitialization: Error getting onboarding state',
          error
        );

        // Only show fallback UI if onboarding should be active
        if (isOnboarding) {
          // Show fallback message on error
          const fallbackMessage = createFallbackOnboardingMessage();
          setDisplayMessage(fallbackMessage);
          setDisplayMode(DisplayMode.ASSISTANT);
          setSuperBarViewState(SuperBarViewState.DEFAULT);

          // Set default onboarding state
          setOnboardingStep(OnboardingStep.PERSONAL_CONNECTION);

          // Dispatch fallback event using the event dispatcher
          eventDispatcher.dispatchMessageReceived({
            onboardingStep: OnboardingStep.PERSONAL_CONNECTION,
          });
        }

        // Mark as completed (even on error, don't retry)
        stateRef.current = OnboardingInitializationState.DONE;
      }
    };
    checkOnboardingState();
  }, [
    currentSession,
    setDisplayMessage,
    setDisplayMode,
    setSuperBarViewState,
    setOnboardingStep,
    eventDispatcher,
    isOnboarding,
    store,
  ]);

  return {
    onboardingInitialized:
      stateRef.current === OnboardingInitializationState.DONE,
  };
}
